import { Dayjs } from 'dayjs';

import { SBaseDisplay, SDate } from '@summit/core';
import {
  DaysOfWeek,
  DaysOfWeekToString,
  MileageFormatter,
  MoneyFormatter,
  NumberSortString,
  RateFormatter
} from '@summit/models';

import { EboObjectTimePeriod } from '../base-classes/ebo-object-time-period';
import { Employee } from '../employee/employee';
import { EmployeePayrollGroup } from '../employee/employee-payroll-group';
import { PayUnit } from '../payroll/pay-unit.enum';
import { EqualityOperator, EqualityOperatorMapping } from '../shared/equality-operator.enum';
import {
  WageEmployeeFilterType,
  WageEmployeeFilterTypeMapping
} from '../shared/payroll/wage-employee-filter-type.enum';
import { Route } from './route';
import { RouteWageStop } from './route-wage-stop';
import { WageTriggerFilter, WageTriggerFilterMapping } from './wage-trigger-filter.enum';
import { Company } from '../organization/company/company';

export class RouteWage extends EboObjectTimePeriod implements SBaseDisplay {
  routeID: string;
  route: Route;
  creatorId: string;
  creator: Employee;
  createdDate: Dayjs;

  //Filters
  employeeFilterType: WageEmployeeFilterType;
  payrollGroupId: string;
  payrollGroup: EmployeePayrollGroup;
  employeeId: string;
  employee: Employee;
  companyId: string;
  company: Company;
  ruleOrder: number;
  days: DaysOfWeek;
  triggerFilter: WageTriggerFilter;
  triggerOperator: EqualityOperator;
  triggerThreshold: number;

  //Pay Logic
  incrementalToWage: boolean;
  considerEmployeeWage: boolean;

  //Pay Amount
  units: PayUnit;
  mileageRateSolo: number;
  mileageRateTeam: number;
  dailyPay: number;
  maxDaysInWeek: number;
  stopWages: RouteWageStop[];
  comment: string;

  get defaultSort() {
    return `${this.endTime ? this.endTime.format('YYYYMMDD') : '00000000'}-${
      this.employee ? this.employee.name : '~'
    }-${this.payrollGroup ? this.payrollGroup.name : '~'}-${
      this.company ? this.company.name : '~'
    }-${this.startTime?.format('YYYYMMDD')}-${NumberSortString(this.ruleOrder, 2)}`;
  }

  constructor(jsonData?: Partial<RouteWage>) {
    super(jsonData);
    if (jsonData) {
      if (jsonData.route) {
        this.route = new Route(jsonData.route);
      }
      if (jsonData.employee) {
        this.employee = new Employee(jsonData.employee);
      }
      if (jsonData.creator) {
        this.creator = new Employee(jsonData.creator);
      }
      if (jsonData.createdDate) {
        this.createdDate = SDate.newUtc(jsonData.createdDate);
      }
      if (jsonData.stopWages) {
        this.stopWages = jsonData.stopWages.map((item) => new RouteWageStop(item));
      }
      if (jsonData.company) this.company = new Company(jsonData.company);

    }
    this.units ??= PayUnit.Dollars;
    this.incrementalToWage ??= false; //Hard False
    this.considerEmployeeWage ??= true; // True for new entries
    this.stopWages ??= new Array<RouteWageStop>();
    this.days ??= DaysOfWeek.AllDays;
    this.triggerFilter ??= WageTriggerFilter.Always;
    this.triggerOperator ??= EqualityOperator.None;
    this.employeeFilterType ??= WageEmployeeFilterType.All;
    if (this.considerEmployeeWage) {
      this.incrementalToWage = false;
    }
  }

  get dayString(): string {
    return DaysOfWeekToString(this.days);
  }

  get triggerSummary(): string {
    if (this.triggerFilter == WageTriggerFilter.Always) return WageTriggerFilterMapping[this.triggerFilter];
    return `${WageTriggerFilterMapping[this.triggerFilter]} ${EqualityOperatorMapping[this.triggerOperator]} ${
      this.triggerThreshold ?? '?'
    }`;
  }

  get applicableEmployee(): string {
    if (this.employeeFilterType == WageEmployeeFilterType.Employee && this.employeeId) return this.employee?.name;
    if (this.employeeFilterType == WageEmployeeFilterType.ByGroup && this.payrollGroupId)
      return this.payrollGroup?.name;
    if (this.employeeFilterType == WageEmployeeFilterType.ByEmployeeCompany && this.companyId)
      return this.company?.name;
    return WageEmployeeFilterTypeMapping[this.employeeFilterType];
  }

  //Mirror C# code
  get paySummary(): string {
    const response = new Array<string>();
    if (this.incrementalToWage) {
      response.push(`Wage +`);
    }
    if (this.units == PayUnit.MileageRate) {
      if (this.mileageRateSolo) {
        response.push(`${RateFormatter(this.mileageRateSolo)} Solo`);
      }
      if (this.mileageRateTeam) {
        response.push(`${RateFormatter(this.mileageRateTeam)} Team`);
      }
    } else {
      if (this.maxDaysInWeek) {
        const weekPay = this.dailyPay * this.maxDaysInWeek;
        if (this.units == PayUnit.Dollars) {
          response.push(`${MoneyFormatter(weekPay)} / Week`);
        } else {
          response.push(`${MileageFormatter(weekPay)} Miles / Week`);
        }
      } else {
        if (this.units == PayUnit.Dollars) {
          response.push(`${MoneyFormatter(this.dailyPay)} / Day`);
        } else {
          if (this.mileageRateSolo) response.push(`${MileageFormatter(this.mileageRateSolo)} Solo`);
          if (this.mileageRateTeam) response.push(`${MileageFormatter(this.mileageRateTeam)} Team`);
          response.push(` Miles / Day`);
        }
      }
    }
    if (!this.incrementalToWage && this.considerEmployeeWage) {
      response.push(' [Min]');
    } else {
      response.push(' [Flat]');
    }
    return response.join(' ');
  }

  toShortString(param?: string): string {
    //console.log(`got param: ${param}`);
    const response = [];
    if (param != 'routePayView') {
      response.push(this.startTime.format('M/D/YY'));
      if (this.endTime) {
        response.push(`-${this.endTime.format('M/D/YY')}`);
      } else {
        response.push(`-Current`);
      }
      response.push(this.route?.toShortString());
    }
    if (this.dailyPay || this.dailyPay == 0) {
      if (this.maxDaysInWeek) {
        response.push(`${MoneyFormatter(this.dailyPay)}/Day (${this.maxDaysInWeek} Max)`);
      } else {
        response.push(`${MoneyFormatter(this.dailyPay)}/Day`);
      }
    } else {
      if (this.mileageRateSolo || this.mileageRateSolo == 0) {
        if (this.mileageRateSolo != 0 && this.incrementalToWage) response.push('+');
        if (this.mileageRateSolo != 0 && this.incrementalToWage == false)
          response.push(`${RateFormatter(this.mileageRateSolo)}S`);
      }
      if (this.mileageRateTeam || this.mileageRateTeam == 0) {
        if (this.mileageRateTeam != 0 && this.incrementalToWage) response.push('+');
        if (this.mileageRateTeam != 0 && this.incrementalToWage == false)
          response.push(`${RateFormatter(this.mileageRateTeam)}T`);
      }
    }
    if (this.stopWages?.length > 0) {
      this.stopWages.forEach((entry) => {
        response.push(`+ ${entry.toShortString()}`);
      });
    }
    if (response.length == 0) response.push('[Empty]');
    return response.join(' ');
  }

  HasDay(flag: DaysOfWeek): boolean {
    // eslint-disable-next-line no-bitwise
    return flag === (this.days & flag);
  }

  SetDay(flag: DaysOfWeek, value: boolean) {
    if (value) {
      // eslint-disable-next-line no-bitwise
      this.days |= flag;
    } else {
      // eslint-disable-next-line no-bitwise
      this.days &= ~flag;
    }
  }
}
